import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PropBotAggregateService } from '@/services/prop-bot/prop-bot-aggregate-service';
import { PropBot, SortOption } from '@/types/prop-bots';
import { useAuth } from '@/contexts/AuthContext';

export interface EnhancedPropBot extends PropBot {
  enhancedStats?: {
    winRate: number;
    maxDrawdown: number;
    potentialProfit: number;
    passRate: number;
    estimatedPassTime: number;
    connectedAccountsTotal: number;
    dataSource: {
      winRate: 'growth_chart' | 'manual';
      maxDrawdown: 'growth_chart' | 'manual';
      potentialProfit: 'growth_chart' | 'manual';
    };
  };
}

export const useEnhancedPropBots = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('pass_rate');

  // OPTIMIZED: Single aggregated query replaces N+1 pattern
  const { data: enhancedBots = [], isLoading, error } = useQuery({
    queryKey: ['propBotsAggregate', user?.id],
    queryFn: () => PropBotAggregateService.fetchAllPropBotsWithStats(user?.id),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in memory for 30 minutes
  });

  const filteredAndSortedBots = useMemo(() => {
    return (enhancedBots as EnhancedPropBot[])
      .filter(bot => 
        bot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bot.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const aValue = a.enhancedStats?.[sortBy] || a.propDetails?.[sortBy] || '0';
        const bValue = b.enhancedStats?.[sortBy] || b.propDetails?.[sortBy] || '0';
        return parseFloat(bValue.toString()) - parseFloat(aValue.toString());
      });
  }, [enhancedBots, searchTerm, sortBy]);

  return {
    bots: filteredAndSortedBots,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy
  };
};